import React, { useState, useEffect } from 'react';
import { LuX, LuSave, LuLoader } from 'react-icons/lu';
import { opportunitiesService } from '../../services/opportunitiesService';
import { accountService } from '../../services/accountService';
import { contactsService } from '../../services/contactsService';
import { OPPORTUNITY_STAGES } from '../../config/supabase';
import SearchableSelect from '../common/SearchableSelect';

const AddOpportunityModal = ({ isOpen, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        name: '',
        amount: '',
        currency: 'USD',
        stage: OPPORTUNITY_STAGES.LEAD,
        close_date: '',
        account_id: '',
        primary_contact_id: '',
        description: ''
    });

    const [accounts, setAccounts] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [loadingContacts, setLoadingContacts] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            fetchAccounts();
            // Reset form when opened
            setFormData({
                name: '',
                amount: '',
                currency: 'USD',
                stage: OPPORTUNITY_STAGES.LEAD,
                close_date: '',
                account_id: '',
                primary_contact_id: '',
                description: ''
            });
            setError('');
        }
    }, [isOpen]);

    // Fetch contacts when account changes
    useEffect(() => {
        if (formData.account_id) {
            fetchContacts(formData.account_id);
        } else {
            setContacts([]);
            setFormData(prev => ({ ...prev, primary_contact_id: '' }));
        }
    }, [formData.account_id]);

    const fetchAccounts = async () => {
        try {
            setLoadingAccounts(true);
            const result = await accountService.getAccounts({ select: 'id, name' });
            if (result.success) {
                setAccounts(result.data);
            } else {
                console.error('Failed to fetch accounts:', result.message);
            }
        } catch (err) {
            console.error('Error fetching accounts:', err);
        } finally {
            setLoadingAccounts(false);
        }
    };

    const fetchContacts = async (accountId) => {
        try {
            setLoadingContacts(true);
            const result = await contactsService.getContactsByAccount(accountId);
            if (result.success) {
                setContacts(result.data);
            } else {
                console.error('Failed to fetch contacts:', result.message);
            }
        } catch (err) {
            console.error('Error fetching contacts:', err);
        } finally {
            setLoadingContacts(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);

        try {
            const result = await opportunitiesService.createOpportunity(formData);

            if (result.success) {
                onSave(); // This will trigger refresh in parent
                onClose();
            } else {
                setError(result.message || 'Failed to create opportunity');
            }
        } catch (err) {
            setError('An unexpected error occurred');
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="z-50 fixed inset-0 overflow-y-auto">
            <div className="sm:block flex justify-center items-center sm:p-0 px-4 pt-4 pb-20 min-h-screen text-center">
                <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                    <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
                </div>

                <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>

                <div className="inline-block bg-white shadow-xl sm:my-8 rounded-lg sm:w-full sm:max-w-lg overflow-hidden text-left sm:align-middle align-bottom transition-all transform">
                    <div className="bg-white sm:p-6 px-4 pt-5 pb-4 sm:pb-4">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="font-medium text-gray-900 text-lg leading-6">
                                Add New Opportunity
                            </h3>
                            <button
                                onClick={onClose}
                                className="focus:outline-none text-gray-400 hover:text-gray-500"
                            >
                                <LuX className="w-6 h-6" />
                            </button>
                        </div>

                        {error && (
                            <div className="bg-red-50 mb-4 p-4 border-red-400 border-l-4">
                                <div className="flex">
                                    <div className="ml-3">
                                        <p className="text-red-700 text-sm">{error}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block font-medium text-gray-700 text-sm">Opportunity Name *</label>
                                <input
                                    type="text"
                                    name="name"
                                    required
                                    value={formData.name}
                                    onChange={handleChange}
                                    className="block shadow-sm mt-1 px-3 py-2 border border-gray-300 focus:border-blue-500 rounded-md focus:outline-none focus:ring-blue-500 w-full sm:text-sm"
                                    placeholder="e.g. Q4 Software License Deal"
                                />
                            </div>

                            <div className="gap-4 grid grid-cols-2">
                                <div>
                                    <label className="block font-medium text-gray-700 text-sm">Amount</label>
                                    <div className="relative shadow-sm mt-1 border border-gray-300 rounded-md">
                                        <div className="left-0 absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                                            <span className="text-gray-500 sm:text-sm">$</span>
                                        </div>
                                        <input
                                            type="number"
                                            name="amount"
                                            min="0"
                                            step="0.01"
                                            value={formData.amount}
                                            onChange={handleChange}
                                            className="block py-2 pr-20 pl-7 border-gray-300 focus:border-blue-500 rounded-md focus:ring-blue-500 w-full sm:text-sm"
                                            placeholder="0.00"
                                        />
                                        <div className="right-0 absolute inset-y-0 flex items-center">
                                            <select
                                                name="currency"
                                                value={formData.currency}
                                                onChange={handleChange}
                                                className="bg-transparent py-0 pr-7 pl-2 border-transparent focus:border-blue-500 rounded-md focus:ring-blue-500 h-full text-gray-500 sm:text-sm"
                                            >
                                                <option>THB</option>
                                                <option>USD</option>
                                                <option>EUR</option>
                                                <option>GBP</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <label className="block font-medium text-gray-700 text-sm">Expected Close Date</label>
                                    <input
                                        type="date"
                                        name="close_date"
                                        value={formData.close_date}
                                        onChange={handleChange}
                                        className="block shadow-sm mt-1 px-3 py-2 border border-gray-300 focus:border-blue-500 rounded-md focus:outline-none focus:ring-blue-500 w-full sm:text-sm"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block font-medium text-gray-700 text-sm">Stage</label>
                                <select
                                    name="stage"
                                    value={formData.stage}
                                    onChange={handleChange}
                                    className="block mt-1 py-2 pr-10 pl-3 border border-gray-300 focus:border-blue-500 rounded-md focus:outline-none focus:ring-blue-500 w-full sm:text-sm text-base"
                                >
                                    {Object.values(OPPORTUNITY_STAGES).map((stage) => (
                                        <option key={stage} value={stage}>{stage}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <SearchableSelect
                                    label="Account"
                                    required
                                    value={formData.account_id}
                                    onChange={(value) => handleChange({ target: { name: 'account_id', value } })}
                                    options={accounts.map(account => ({
                                        value: account.id,
                                        label: account.name
                                    }))}
                                    placeholder={loadingAccounts ? "Loading accounts..." : "Search or select an account..."}
                                    disabled={loadingAccounts}
                                />
                            </div>

                            <div>
                                <SearchableSelect
                                    label="Contact"
                                    value={formData.primary_contact_id}
                                    onChange={(value) => handleChange({ target: { name: 'primary_contact_id', value } })}
                                    options={contacts.map(contact => ({
                                        value: contact.id,
                                        label: `${contact.first_name} ${contact.last_name}`
                                    }))}
                                    placeholder={
                                        !formData.account_id
                                            ? "Select an account first"
                                            : loadingContacts
                                                ? "Loading contacts..."
                                                : "Search or select a contact..."
                                    }
                                    disabled={!formData.account_id || loadingContacts}
                                />
                            </div>

                            <div>
                                <label className="block font-medium text-gray-700 text-sm">Description / Notes</label>
                                <textarea
                                    name="description"
                                    rows="3"
                                    value={formData.description}
                                    onChange={handleChange}
                                    className="block shadow-sm mt-1 px-3 py-2 border border-gray-300 focus:border-blue-500 rounded-md focus:outline-none focus:ring-blue-500 w-full sm:text-sm"
                                    placeholder="Additional details..."
                                ></textarea>
                            </div>

                            <div className="sm:gap-3 sm:grid sm:grid-cols-2 sm:grid-flow-row-dense mt-5 sm:mt-6">
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="inline-flex justify-center sm:col-start-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 shadow-sm px-4 py-2 border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 w-full font-medium text-white sm:text-sm text-base"
                                >
                                    {submitting ? (
                                        <>
                                            <LuLoader className="mr-2 -ml-1 w-5 h-5 animate-spin" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <LuSave className="mr-2 -ml-1 w-5 h-5" />
                                            Save Opportunity
                                        </>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="inline-flex justify-center sm:col-start-1 bg-white hover:bg-gray-50 shadow-sm mt-3 sm:mt-0 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 w-full font-medium text-gray-700 sm:text-sm text-base"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddOpportunityModal;
